import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useNavigate, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { cart, fmtEuro } from "@/lib/cart";
import { ShoppingCart, ChevronLeft, Loader2, CheckCircle2, Truck, MapPin } from "lucide-react";

const FREE_SHIPPING_THRESHOLD = 10000; // 100,00 €
const SHIPPING_FEE = 590;              // 5,90 €
const VAT_RATE = 19;

export default function ShopCheckout() {
  const navigate = useNavigate();
  const [items, setItems] = useState(cart.items);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  // Pre-fill form from the user's profile
  const [form, setForm] = useState({
    shipping_name: "", shipping_company: "", shipping_address_line: "",
    shipping_postal_code: "", shipping_city: "", shipping_country: "Deutschland",
    contact_email: "", contact_phone: "", notes: "",
  });

  useEffect(() => cart.subscribe(setItems), []);

  useEffect(() => {
    api.get("/users/me/profile").then(({ data }) => {
      const u = data.user || {};
      setForm((f) => ({
        ...f,
        shipping_name: u.name || f.shipping_name,
        shipping_company: u.company || "",
        shipping_address_line: u.address_line || "",
        shipping_postal_code: u.postal_code || "",
        shipping_city: u.city || "",
        shipping_country: u.country || "Deutschland",
        contact_email: u.email || "",
        contact_phone: u.mobile || u.phone || "",
      }));
    }).catch(() => { /* ignore — user can fill manually */ });
  }, []);

  const subtotal = useMemo(() => items.reduce((s, i) => s + i.unit_price_cents * i.qty, 0), [items]);
  const shipping = subtotal >= FREE_SHIPPING_THRESHOLD ? 0 : (items.length ? SHIPPING_FEE : 0);
  const vat = Math.round((subtotal + shipping) * VAT_RATE / 100);
  const total = subtotal + shipping;

  const placeOrder = async () => {
    if (items.length === 0) return toast.error("Warenkorb ist leer");
    const required = ["shipping_name", "shipping_address_line", "shipping_postal_code", "shipping_city", "contact_email"];
    for (const k of required) {
      if (!form[k]?.toString().trim()) return toast.error("Bitte alle Pflichtfelder ausfüllen");
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        items: items.map((it) => ({
          product_id: it.product_id, sku: it.sku, name: it.name,
          qty: it.qty, unit_price_cents: it.unit_price_cents,
          line_total_cents: it.unit_price_cents * it.qty,
          size: it.size || null, color: it.color_choice || null,
        })),
      };
      const { data } = await api.post("/shop/orders", payload);
      cart.clear();
      setConfirmation(data.order);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  // Confirmation screen
  if (confirmation) {
    return (
      <Layout title="Bestellung bestätigt" subtitle={`${confirmation.order_number} · Vielen Dank`}>
        <div className="max-w-2xl mx-auto">
          <Card className="p-8 text-center" data-testid="shop-checkout-confirmation">
            <div className="h-16 w-16 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h2 className="font-display text-2xl font-bold tracking-tight">Bestellung eingegangen!</h2>
            <p className="text-zinc-500 mt-2">
              Deine Bestell-Nr. <span className="font-mono font-bold text-zinc-900">{confirmation.order_number}</span>{" "}
              wird in Kürze bearbeitet. Eine Bestätigungsmail kommt an{" "}
              <span className="font-medium">{confirmation.contact_email}</span>.
            </p>
            <div className="mt-6 grid sm:grid-cols-2 gap-3 text-left">
              <Card className="p-4 bg-zinc-50/50">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500">Lieferadresse</div>
                <div className="text-sm mt-1 leading-snug">
                  {confirmation.shipping_name}<br />
                  {confirmation.shipping_company && <>{confirmation.shipping_company}<br /></>}
                  {confirmation.shipping_address_line}<br />
                  {confirmation.shipping_postal_code} {confirmation.shipping_city}<br />
                  {confirmation.shipping_country}
                </div>
              </Card>
              <Card className="p-4 bg-zinc-50/50">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500">Zusammenfassung</div>
                <dl className="text-sm space-y-1 mt-1">
                  <div className="flex justify-between"><dt>Zwischensumme</dt><dd className="tabular-nums">{fmtEuro(confirmation.subtotal_cents)}</dd></div>
                  <div className="flex justify-between"><dt>Versand</dt><dd className="tabular-nums">{confirmation.shipping_cents === 0 ? "Gratis" : fmtEuro(confirmation.shipping_cents)}</dd></div>
                  <div className="flex justify-between font-display font-bold border-t border-zinc-200 pt-1.5 mt-1.5"><dt>Gesamt netto</dt><dd className="tabular-nums">{fmtEuro(confirmation.total_cents)}</dd></div>
                </dl>
              </Card>
            </div>
            <div className="flex flex-wrap gap-2 justify-center mt-6">
              <Link to="/shop"><Button variant="outline">Weiter shoppen</Button></Link>
              <Link to="/shop/orders"><Button>Bestellungen ansehen</Button></Link>
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Bestellung abschließen" subtitle="Lieferung an deine im Profil hinterlegten Daten">
      <div className="max-w-6xl">
        <Link to="/shop" className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 mb-4" data-testid="shop-back-link">
          <ChevronLeft className="h-4 w-4" /> Zurück zum Shop
        </Link>

        <div className="grid lg:grid-cols-[1fr_360px] gap-6">
          {/* Form */}
          <div className="space-y-4">
            <Card className="p-5" data-testid="checkout-shipping-form">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="h-4 w-4 text-emerald-600" />
                <h3 className="font-display text-lg font-bold tracking-tight">Lieferadresse</h3>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Empfänger *"><Input value={form.shipping_name} onChange={(e) => setForm({ ...form, shipping_name: e.target.value })} data-testid="checkout-name" /></Field>
                <Field label="Firma"><Input value={form.shipping_company} onChange={(e) => setForm({ ...form, shipping_company: e.target.value })} data-testid="checkout-company" /></Field>
                <div className="sm:col-span-2"><Field label="Straße & Hausnummer *"><Input value={form.shipping_address_line} onChange={(e) => setForm({ ...form, shipping_address_line: e.target.value })} data-testid="checkout-address" /></Field></div>
                <Field label="PLZ *"><Input value={form.shipping_postal_code} onChange={(e) => setForm({ ...form, shipping_postal_code: e.target.value })} data-testid="checkout-postal" /></Field>
                <Field label="Stadt *"><Input value={form.shipping_city} onChange={(e) => setForm({ ...form, shipping_city: e.target.value })} data-testid="checkout-city" /></Field>
                <div className="sm:col-span-2"><Field label="Land"><Input value={form.shipping_country} onChange={(e) => setForm({ ...form, shipping_country: e.target.value })} data-testid="checkout-country" /></Field></div>
              </div>
            </Card>

            <Card className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <Truck className="h-4 w-4 text-emerald-600" />
                <h3 className="font-display text-lg font-bold tracking-tight">Kontakt für Versand</h3>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="E-Mail *"><Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="checkout-email" /></Field>
                <Field label="Telefon"><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="checkout-phone" /></Field>
                <div className="sm:col-span-2">
                  <Field label="Notizen / Wünsche zum Druck">
                    <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="z. B. Logo-Datei vorhanden, Sondergröße, Lieferdatum…" data-testid="checkout-notes" />
                  </Field>
                </div>
              </div>
            </Card>
          </div>

          {/* Summary */}
          <div>
            <Card className="p-5 sticky top-4" data-testid="checkout-summary">
              <div className="flex items-center gap-2 mb-3">
                <ShoppingCart className="h-4 w-4 text-emerald-600" />
                <h3 className="font-display text-lg font-bold tracking-tight">Bestellübersicht</h3>
              </div>
              {items.length === 0 ? (
                <div className="text-sm text-zinc-400 py-6 text-center">Warenkorb leer.</div>
              ) : (
                <ul className="space-y-2 mb-4 max-h-60 overflow-y-auto pr-1">
                  {items.map((it) => (
                    <li key={it.lineKey} className="text-[12px] flex items-start gap-2 border-b border-zinc-100 pb-2 last:border-0">
                      <span className="font-mono w-6 text-right text-zinc-400">{it.qty}×</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{it.name}</div>
                        {(it.size || it.color_choice) && <div className="text-zinc-400 text-[11px]">{[it.size && `Gr. ${it.size}`, it.color_choice].filter(Boolean).join(" · ")}</div>}
                      </div>
                      <span className="font-mono tabular-nums">{fmtEuro(it.unit_price_cents * it.qty)}</span>
                    </li>
                  ))}
                </ul>
              )}
              <dl className="space-y-1 text-sm border-t border-zinc-100 pt-3">
                <div className="flex justify-between"><dt className="text-zinc-500">Zwischensumme</dt><dd className="tabular-nums font-mono">{fmtEuro(subtotal)}</dd></div>
                <div className="flex justify-between">
                  <dt className="text-zinc-500">Versand</dt>
                  <dd className="tabular-nums font-mono">{shipping === 0 ? <span className="text-emerald-600 font-bold">Gratis</span> : fmtEuro(shipping)}</dd>
                </div>
                <div className="flex justify-between text-[11px] text-zinc-400"><dt>davon USt {VAT_RATE}%</dt><dd className="tabular-nums font-mono">{fmtEuro(vat)}</dd></div>
                <div className="flex justify-between font-display font-bold text-base pt-2 border-t border-zinc-200 mt-1">
                  <dt>Gesamt netto</dt>
                  <dd className="tabular-nums font-mono">{fmtEuro(total)}</dd>
                </div>
              </dl>
              <Button
                className="w-full mt-4"
                size="lg"
                onClick={placeOrder}
                disabled={submitting || items.length === 0}
                data-testid="checkout-place-order"
              >
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Jetzt verbindlich bestellen
              </Button>
              <p className="text-[10px] text-zinc-400 mt-2 text-center">
                Mit Klick akzeptierst du unsere <Link to="/datenschutz" className="underline">Datenschutzerklärung</Link>.
              </p>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
